
import { useState } from "react"
import { Check, Copy, Mail } from "lucide-react"

import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/**
 * An email address people can act on. A bare `mailto:` opens whatever mail
 * app the computer happens to have set up (often an Outlook nobody uses), so
 * this asks: Gmail or Outlook on the web, the default app, or just copy it.
 */
export function EmailLink({
  email,
  subject,
  children,
  className,
  align = "start",
}: {
  email: string
  subject?: string
  children?: React.ReactNode
  className?: string
  align?: "start" | "center" | "end"
}) {
  const [copied, setCopied] = useState(false)
  const to = encodeURIComponent(email)
  const su = subject ? encodeURIComponent(subject) : ""

  const options = [
    {
      label: "Gmail",
      logo: "/mail/gmail.svg",
      href: `https://mail.google.com/mail/?view=cm&fs=1&to=${to}${su ? `&su=${su}` : ""}`,
    },
    {
      label: "Outlook",
      logo: "/mail/outlook.svg",
      // outlook.office.com serves work accounts and forwards personal ones.
      href: `https://outlook.office.com/mail/deeplink/compose?to=${to}${su ? `&subject=${su}` : ""}`,
    },
  ]

  async function copy() {
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* clipboard blocked: the address is shown in the menu anyway */
    }
  }

  const item =
    "flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cn("cursor-pointer text-left", className)}>
          {children ?? email}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 p-1.5">
        <p className="px-2.5 pb-1.5 pt-1 text-xs text-muted-foreground">
          Email <span className="font-medium text-foreground">{email}</span> with
        </p>
        {options.map((o) => (
          <a key={o.label} href={o.href} target="_blank" rel="noopener noreferrer" className={item}>
            {/* eslint-disable-next-line @next/next/no-img-element -- small static logos */}
            <img src={o.logo} alt="" width={18} height={18} className="h-[18px] w-[18px]" />
            {o.label}
          </a>
        ))}
        <a href={`mailto:${email}${su ? `?subject=${su}` : ""}`} className={item}>
          <Mail className="h-[18px] w-[18px] text-muted-foreground" />
          Default email app
        </a>
        <div className="my-1 h-px bg-border" />
        <button type="button" onClick={copy} className={item}>
          {copied ? (
            <Check className="h-[18px] w-[18px] text-emerald-600" />
          ) : (
            <Copy className="h-[18px] w-[18px] text-muted-foreground" />
          )}
          {copied ? "Copied" : "Copy address"}
        </button>
      </PopoverContent>
    </Popover>
  )
}
